import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { Link } from 'react-router-dom';

import ErrorBlock from '../../../common/ErrorBlock';
import { useGetVouchers } from '../../../hooks/queries/useVoucherQuery';
import { TicketIcon } from '../../../icons/TicketIcon';
import { formatError } from '../../../util/errors';
import { PATH_HUB_VOUCHERS } from '../../../util/pagePath';
import { BookOneOffSession } from './BookOneOffSession';

export interface VoucherWidgetProps {
  className?: string;
}

export const VoucherWidget = ({ className }: VoucherWidgetProps) => {
  const { data: vouchers, isLoading: isVoucherLoading, error: vouchersError } = useGetVouchers('Active');

  if (isEmpty(vouchers) && !isVoucherLoading) {
    return null;
  }

  return (
    <div
      className={classNames(
        'mb-10 flex flex-col rounded-lg border border-slate-2 bg-white px-6 py-5 shadow-md lg:mb-8 lg:p-10 lg:py-8',
        className,
        {
          hidden: isVoucherLoading,
        }
      )}
    >
      {vouchersError && <ErrorBlock errorMsg={formatError(vouchersError)} />}
      {vouchers && (
        <>
          <div className="flex flex-row gap-3 lg:flex-row">
            <div className="flex !w-6 items-start justify-center">
              <TicketIcon width={24} height={24} className="mt-[2px] fill-blood-orange-4" />
            </div>
            <div className="flex flex-col items-start gap-2 lg:basis-4/5">
              <div className="flex items-center gap-3 text-lg font-bold lg:gap-4">
                <p className="text-lg font-bold">
                  {vouchers.length} Cluey Voucher{vouchers.length > 1 ? 's' : ''} available
                </p>
              </div>
              <div className="flex flex-col gap-2 lg:flex-row">
                <span className="gap-y-1">
                  Use your voucher{vouchers.length > 1 ? 's' : ''} to book make-up sessions before they expire &nbsp;
                  <Link className="hidden !text-base text-blue-5 underline lg:inline-block" to={PATH_HUB_VOUCHERS}>
                    View vouchers
                  </Link>
                </span>
                <Link
                  className="flex !text-base text-blue-5 underline lg:hidden lg:justify-end lg:text-right"
                  to={PATH_HUB_VOUCHERS}
                >
                  View vouchers
                </Link>
              </div>
              <div className="hidden !text-sm">
                <p>
                  Did you know you can use vouchers for any subject or student in your household?{' '}
                  <Link className="pl-1 text-blue-5 underline" to={PATH_HUB_VOUCHERS}>
                    Start trial
                  </Link>
                </p>
              </div>
            </div>
            <div className="hidden flex-col items-center gap-2 lg:flex lg:basis-1/5">
              <div className="my-auto flex w-full items-center lg:w-[225px]">
                <BookOneOffSession
                  buttonClassName="w-full rounded-full bg-primary px-4 py-2 text-blue-5 hover:text-white lg:w-auto"
                  buttonAppearance="reverse"
                />
              </div>
              <Link
                className="hidden !text-base text-blue-5 underline lg:justify-end lg:text-right"
                to={PATH_HUB_VOUCHERS}
              >
                View vouchers
              </Link>
            </div>
          </div>
          <div className="mt-4 flex lg:hidden">
            <div className="flex w-full items-center">
              <BookOneOffSession
                buttonClassName="w-full rounded-full bg-primary px-4 py-2 text-blue-5 hover:text-white lg:w-auto"
                buttonAppearance="reverse"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
